"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import { ApiNotificationCause } from "@/generated/models/ApiNotificationCause";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import NotificationsCauseFilter from "./NotificationsCauseFilter";
import { useNotificationsController } from "./hooks/useNotificationsController";
import { useNotificationsScroll } from "./hooks/useNotificationsScroll";
import NotificationsContent from "./subcomponents/NotificationsContent";
interface NotificationsProps {
  readonly activeDrop: ActiveDropState | null;
  readonly setActiveDrop: (activeDrop: ActiveDropState | null) => void;
}
const NOTIFICATION_CAUSE_PRIORITY: Record<ApiNotificationCause, number> = {
  [ApiNotificationCause.IdentitySubscribed]: 0,
  [ApiNotificationCause.IdentityMentioned]: 1,
  [ApiNotificationCause.DropQuoted]: 2,
  [ApiNotificationCause.DropReplied]: 3,
  [ApiNotificationCause.DropVoted]: 4,
  [ApiNotificationCause.DropReacted]: 5,
  [ApiNotificationCause.WaveCreated]: 6,
  [ApiNotificationCause.AllDrops]: 7
};
const compareNotificationCause = (firstCause: ApiNotificationCause, secondCause: ApiNotificationCause): number => NOTIFICATION_CAUSE_PRIORITY[firstCause] - NOTIFICATION_CAUSE_PRIORITY[secondCause];
export default function Notifications(t0) {
  const $ = _c(43);
  const {
    activeDrop,
    setActiveDrop
  } = t0;
  const {
    activeFilter,
    setActiveFilter,
    isAuthenticated,
    notificationsViewStyle,
    items,
    isFetchingNextPage,
    pagination,
    contentState,
    handlers
  } = useNotificationsController();
  let t1;
  if ($[0] !== activeFilter) {
    t1 = activeFilter?.cause ? [...activeFilter.cause].sort(compareNotificationCause).join("|") : "notifications-filter-all";
    $[0] = activeFilter;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const activeFilterKey = t1;
  let t2;
  if ($[2] !== activeFilterKey || $[3] !== contentState.showErrorState || $[4] !== contentState.showLoader || $[5] !== contentState.showNoItems || $[6] !== isAuthenticated || $[7] !== isFetchingNextPage || $[8] !== items || $[9] !== pagination.fetchNextPage || $[10] !== pagination.hasNextPage) {
    t2 = {
      items,
      isAuthenticated,
      isFetchingNextPage,
      hasNextPage: pagination.hasNextPage,
      fetchNextPage: pagination.fetchNextPage,
      showLoader: contentState.showLoader,
      showNoItems: contentState.showNoItems,
      showErrorState: contentState.showErrorState,
      activeFilterKey
    };
    $[2] = activeFilterKey;
    $[3] = contentState.showErrorState;
    $[4] = contentState.showLoader;
    $[5] = contentState.showNoItems;
    $[6] = isAuthenticated;
    $[7] = isFetchingNextPage;
    $[8] = items;
    $[9] = pagination.fetchNextPage;
    $[10] = pagination.hasNextPage;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const {
    scrollContainerRef,
    handleScroll
  } = useNotificationsScroll(t2);
  let t3;
  if ($[12] !== activeFilter || $[13] !== isAuthenticated || $[14] !== setActiveFilter) {
    t3 = isAuthenticated ? <NotificationsCauseFilter activeFilter={activeFilter} setActiveFilter={setActiveFilter} /> : null;
    $[12] = activeFilter;
    $[13] = isAuthenticated;
    $[14] = setActiveFilter;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  let t4;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      WebkitOverflowScrolling: "touch"
    };
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== activeDrop || $[18] !== contentState.hasConnectedProfile || $[19] !== contentState.hasProfileHandle || $[20] !== contentState.isLoadingProfile || $[21] !== contentState.resolvedErrorMessage || $[22] !== contentState.showErrorState || $[23] !== contentState.showLoader || $[24] !== contentState.showNoItems || $[25] !== contentState.showProxyDisabledState || $[26] !== handlers.handleAuthRetry || $[27] !== handlers.handleProxyDisable || $[28] !== handlers.handleRetry || $[29] !== isFetchingNextPage || $[30] !== items || $[31] !== setActiveDrop) {
    t5 = <NotificationsContent isLoadingProfile={contentState.isLoadingProfile} hasConnectedProfile={contentState.hasConnectedProfile} hasProfileHandle={contentState.hasProfileHandle} showProxyDisabledState={contentState.showProxyDisabledState} showErrorState={contentState.showErrorState} resolvedErrorMessage={contentState.resolvedErrorMessage} handleRetry={handlers.handleRetry} handleAuthRetry={handlers.handleAuthRetry} handleProxyDisable={handlers.handleProxyDisable} showLoader={contentState.showLoader} showNoItems={contentState.showNoItems} items={items} loadingOlder={isFetchingNextPage} activeDrop={activeDrop} setActiveDrop={setActiveDrop} />;
    $[17] = activeDrop;
    $[18] = contentState.hasConnectedProfile;
    $[19] = contentState.hasProfileHandle;
    $[20] = contentState.isLoadingProfile;
    $[21] = contentState.resolvedErrorMessage;
    $[22] = contentState.showErrorState;
    $[23] = contentState.showLoader;
    $[24] = contentState.showNoItems;
    $[25] = contentState.showProxyDisabledState;
    $[26] = handlers.handleAuthRetry;
    $[27] = handlers.handleProxyDisable;
    $[28] = handlers.handleRetry;
    $[29] = isFetchingNextPage;
    $[30] = items;
    $[31] = setActiveDrop;
    $[32] = t5;
  } else {
    t5 = $[32];
  }
  let t6;
  if ($[33] !== handleScroll || $[34] !== scrollContainerRef || $[35] !== t5) {
    t6 = <div ref={scrollContainerRef} onScroll={handleScroll} className="tw-flex tw-flex-1 tw-flex-col tw-overflow-y-auto tw-overflow-x-hidden tw-scrollbar-thin tw-scrollbar-thumb-iron-500 tw-scrollbar-track-iron-800 desktop-hover:hover:tw-scrollbar-thumb-iron-300" style={t4}>{t5}</div>;
    $[33] = handleScroll;
    $[34] = scrollContainerRef;
    $[35] = t5;
    $[36] = t6;
  } else {
    t6 = $[36];
  }
  let t7;
  if ($[37] !== t3 || $[38] !== t6) {
    t7 = <div className="tw-flex-1 tw-h-full tw-relative tw-flex-col tw-flex tw-px-2 sm:tw-px-4 md:tw-px-6 lg:tw-px-0">{t3}{t6}</div>;
    $[37] = t3;
    $[38] = t6;
    $[39] = t7;
  } else {
    t7 = $[39];
  }
  let t8;
  if ($[40] !== notificationsViewStyle || $[41] !== t7) {
    t8 = <div className="tw-relative tw-flex tw-flex-col tw-rounded-t-xl tw-overflow-x-hidden scroll-shadow" style={notificationsViewStyle}>{t7}</div>;
    $[40] = notificationsViewStyle;
    $[41] = t7;
    $[42] = t8;
  } else {
    t8 = $[42];
  }
  return t8;
}